// Stay Vitals — at-a-glance fact strip for every stay detail page.
// Variant C: magazine-spread, asymmetric, hero-numeral.
import React from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtSeason = (months) => {
  if (!months || months.length === 0) return null;
  const sorted = [...months].sort((a, b) => a - b);
  const runs = [];
  let s = sorted[0];
  let p = s;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === p + 1) { p = sorted[i]; continue; }
    runs.push([s, p]);
    s = p = sorted[i];
  }
  runs.push([s, p]);
  return runs.map(([a, b]) => a === b ? MONTHS[a - 1] : `${MONTHS[a - 1]}–${MONTHS[b - 1]}`).join(', ');
};

const fmtINR = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN');

const titleCase = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Tag → icon mapping for the amenities row.
// Supports both normalized keys ("wood_stove") and free-form scraped labels
// ("River Access", "Mountain Views"). The matcher slugifies the input before
// looking it up, then falls back to partial token matching.
const AMENITY_ICONS = {
  // Heat / warmth
  wood_stove: '🔥', fireplace: '🔥', bonfire: '🔥', bonfire_nights: '🔥',
  bonfire_pit: '🔥', wood_burner: '🔥', heater: '♨️', hot_water: '🚿',
  hot_water_24_7: '🚿', heating: '♨️', electric_backup: '⚡',
  electric_back_up: '⚡', electric_backup_power: '⚡',
  // Connectivity / utilities
  wifi: '📶', wi_fi: '📶', free_wifi: '📶', parking: '🅿️', free_parking: '🅿️',
  parking_on_site: '🅿️', ev_charging: '🔌',
  // Pets / family
  pet_friendly: '🐕', pets: '🐕', pets_allowed: '🐕',
  child_friendly: '👶', family_friendly: '👶',
  kids: '🧸', kids_play: '🧸', kids_play_area: '🧸',
  wheelchair: '♿', wheelchair_accessible: '♿',
  // Food
  breakfast: '🍳', breakfast_included: '🍳',
  all_meals: '🍽️', meals_included: '🍽️', home_cooked_meals: '🍽️',
  homecooked: '🍽️', dining: '🍽️',
  in_house_chef: '👨‍🍳', chef: '👨‍🍳',
  kitchen: '🥘', shared_kitchen: '🥘', full_kitchen_access: '🥘',
  cooking: '🥘', cooking_with_host: '🥘',
  bbq: '🍖', bbq_grill: '🍖', grill: '🍖',
  // Indoor / amenities
  library: '📚', books: '📚', reading_room: '📚', library_book_corner: '📚',
  sit_out: '☕', veranda: '☕', sit_out_veranda: '☕', balcony: '☕',
  // Nature & views
  garden: '🌿', garden_lawn: '🌿', lawn: '🌿', kitchen_garden: '🌿',
  orchard: '🍎', orchards: '🍎',
  river_access: '🌊', river: '🌊', riverside: '🌊', river_view: '🌊',
  river_swimming: '🌊', river_swimming_dip: '🌊', lake_access: '🌊',
  mountain_views: '⛰️', mountain_view: '⛰️', views: '⛰️',
  forest: '🌲', forest_walks: '🌲',
  // Outdoor activities
  trekking: '🥾', trekking_from_doorstep: '🥾', hiking: '🥾',
  nature_trails: '🥾', nature_walks: '🥾',
  fishing: '🎣', trout_fishing: '🎣',
  birding: '🦜', bird_watching: '🦜', birdwatching: '🦜', birds: '🦜',
  yoga: '🧘', yoga_deck: '🧘', meditation: '🧘', yoga_meditation: '🧘',
  spa: '💆',
  stargazing: '✨', star_gazing: '✨', astronomy: '✨',
  photography: '📷',
  biking: '🚴', mountain_biking: '🚴', cycling: '🚴',
  village: '🚶', village_walk: '🚶', local_village_walk: '🚶',
  sunrise: '🌅', sunset: '🌅', sunrise_sunset_point: '🌅', sunrise_point: '🌅',
  picnic: '🧺', picnic_spot: '🧺', picnic_spot_nearby: '🧺',
  mela: '🎪', market: '🎪', local_market: '🎪', local_market_mela: '🎪',
  cultural: '🎨', craft: '🎨', cultural_craft_session: '🎨',
  skiing: '⛷️', heli_skiing: '🚁', heli_skiing_nearby: '🚁',
  camping: '⛺', camping_option: '⛺',
  campfire: '🔥',
};

const slugifyKey = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const iconFor = (key) => {
  if (!key) return '◆';
  const slug = slugifyKey(key);
  if (AMENITY_ICONS[slug]) return AMENITY_ICONS[slug];
  // Partial-token match — e.g. "free_wifi" / "wifi_available" → 📶
  for (const tk of Object.keys(AMENITY_ICONS)) {
    if (slug.includes(tk) || tk.includes(slug)) return AMENITY_ICONS[tk];
  }
  return '◆';
};

export default function StayVitals({ stay }) {
  if (!stay) return null;

  // Open to all tiers (featured / partner / valley_partner / discovered).
  // Strip auto-hides at the bottom of this function if no Tier-1 field is
  // present, so a stay with no inventory data still won't get an empty bar.

  const unitCount = stay.unit_count;
  // Sanitise unit_type — some legacy AI-drafts stored duplicates like
  // "family rooms & rooms". Split on ' & ', dedupe case-insensitively, rejoin.
  const _rawUnitType = stay.unit_type || (stay.property_type ? stay.property_type.toLowerCase() : 'units');
  const unitType = (() => {
    const parts = String(_rawUnitType).split(/\s*&\s*/).map(p => p.trim()).filter(Boolean);
    const seen = new Set();
    const clean = [];
    for (const p of parts) {
      const k = p.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      clean.push(p);
    }
    return clean.join(' & ') || 'units';
  })();
  const sleeps = stay.sleeps_max;
  const price = stay.starting_price_inr;
  // Compose the price label from the structured fields (with_breakfast +
  // price_unit). Falls back to the legacy free-text `price_includes` if set.
  // Avoids the old "per night · per night" duplication bug.
  const buildPriceLabel = () => {
    const unit = stay.price_unit === 'per_person' ? 'per person · per night'
               : stay.price_unit === 'whole_property' ? 'whole property · per night'
               : 'per night';
    if (stay.includes_breakfast === true) return `with breakfast · ${unit}`;
    if (stay.includes_breakfast === false) return unit;
    const pi = (stay.price_includes || '').trim();
    if (!pi || pi.toLowerCase() === 'per night') return unit;
    return `${pi} · ${unit}`;
  };
  const priceLabel = buildPriceLabel();
  const altitude = stay.altitude_meters;
  const season = fmtSeason(stay.best_months);
  const bestFor = (stay.best_for || []).slice(0, 3).map(titleCase);
  // Merge structured amenities + experiences from the new chip-form on /apply.
  // Dedup case-insensitively so a curator who imported amenities as
  // "Fireplace" can't double-count it against an applicant's "fireplace".
  const _rawAmenities = stay.amenities || [];
  const _rawExperiences = stay.experiences || [];
  const _seen = new Set();
  const amenities = [];
  for (const item of [..._rawAmenities, ..._rawExperiences]) {
    const key = typeof item === 'object'
      ? String(item.key || item.label || '').toLowerCase().trim()
      : String(item || '').toLowerCase().trim();
    if (!key || _seen.has(key)) continue;
    _seen.add(key);
    amenities.push(item);
  }

  // Hide the entire section if we have nothing meaningful to show
  const hasTier1 = unitCount || price || sleeps;
  if (!hasTier1) return null;

  // Determine how many big columns we'll render
  const cols = [unitCount, price, sleeps].filter(Boolean).length;
  const colSpan = cols === 3 ? 'md:col-span-4' : cols === 2 ? 'md:col-span-6' : 'md:col-span-12';

  return (
    <div
      className="bg-amber-50/40 py-14 md:py-16 px-6 md:px-16 border-y border-amber-900/10"
      data-testid="stay-vitals"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
        {/* Tier 1A — Unit count + type */}
        {unitCount ? (
          <div className={colSpan} data-testid="vitals-units">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60">A stay of</div>
            <div className="flex items-baseline gap-3 mt-2 flex-wrap">
              <div className="font-serif text-7xl md:text-[7rem] text-stone-900 leading-none">
                {unitCount}
              </div>
              <div className="text-2xl md:text-3xl font-serif text-stone-700 italic">
                {unitType}
              </div>
            </div>
            {(sleeps || stay.property_type) && (
              <div className="mt-4 text-sm text-stone-600">
                {sleeps && <>Sleeps <span className="font-semibold text-stone-900">{sleeps}</span></>}
                {sleeps && stay.property_type && ' · '}
                {stay.property_type && titleCase(stay.property_type)}
              </div>
            )}
          </div>
        ) : null}

        {/* Tier 1B — Starting price */}
        {price ? (
          <div className={`${colSpan} ${unitCount ? 'md:border-l md:pl-12 border-amber-900/15' : ''}`} data-testid="vitals-price">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60">From</div>
            <div className="font-serif text-6xl md:text-7xl text-stone-900 leading-none mt-2">
              {fmtINR(price)}
            </div>
            <div className="mt-3 text-sm tracking-wide text-stone-600 italic">
              {priceLabel}
            </div>
          </div>
        ) : null}

        {/* Tier 1C — Sleeps (only as solo column if unitCount didn't carry it) */}
        {sleeps && !unitCount ? (
          <div className={`${colSpan} ${price ? 'md:border-l md:pl-12 border-amber-900/15' : ''}`} data-testid="vitals-sleeps">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60">Sleeps up to</div>
            <div className="font-serif text-6xl md:text-7xl text-stone-900 leading-none mt-2">
              {sleeps}
            </div>
            <div className="mt-3 text-sm tracking-wide text-stone-600">guests · max</div>
          </div>
        ) : null}

        {/* Good to know panel (Tier 2) — render only if 3-col layout, otherwise fold into a row below */}
        {cols === 3 && (altitude || season || bestFor.length > 0) ? (
          <div className="md:col-span-4 md:border-l md:pl-12 border-amber-900/15" data-testid="vitals-meta">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-3">Good to know</div>
            <ul className="space-y-2 text-sm text-stone-700">
              {altitude && (
                <li>
                  <span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Altitude</span>
                  {altitude.toLocaleString('en-IN')} m
                </li>
              )}
              {season && (
                <li>
                  <span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Season</span>
                  {season}
                </li>
              )}
              {bestFor.length > 0 && (
                <li>
                  <span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Ideal</span>
                  {bestFor.join(', ')}
                </li>
              )}
            </ul>
          </div>
        ) : null}
      </div>

      {/* Fallback "good to know" line for 2-col layout */}
      {cols < 3 && (altitude || season || bestFor.length > 0) ? (
        <div className="max-w-6xl mx-auto mt-8 pt-6 border-t border-amber-900/10 flex flex-wrap gap-x-10 gap-y-2 text-sm text-stone-700">
          {altitude && (
            <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Altitude</span>{altitude.toLocaleString('en-IN')} m</span>
          )}
          {season && (
            <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Season</span>{season}</span>
          )}
          {bestFor.length > 0 && (
            <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Ideal</span>{bestFor.join(', ')}</span>
          )}
        </div>
      ) : null}

      {/* Tier 3 — Amenity icon row */}
      {amenities.length > 0 ? (
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-amber-900/10 flex flex-wrap gap-x-8 gap-y-3" data-testid="vitals-amenities">
          {amenities.slice(0, 16).map((a, i) => {
            const isObj = typeof a === 'object' && a !== null;
            const key = isObj ? (a.key || a.label || '') : a;
            const label = isObj ? (a.label || titleCase(a.key)) : titleCase(a);
            const icon = isObj && a.icon ? a.icon : iconFor(key);
            return (
              <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
                <span className="text-lg">{icon}</span>
                <span className="tracking-wide">{label}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
